import { IAteParents, setLastAteParams } from 'store/actions/layers/constants';
import {
  needUpdateAction,
  setBackdropLoadingAction,
  toggleOpenClusterModalAction,
  toggleOpenLayerModalAction,
} from 'store/actions/mainPage';
import { AppDispatch } from 'store/index';
import { layersSlice } from 'store/reducers/layers';
import { ISelectedATE } from 'types/mainPage';
import { getText, LanguageService } from 'services/languageService';
import { httpClient, httpConfig } from 'utils/httpClient';
import { getAllChildren } from 'components/LayersTab/constants';
import { getClusterInfoVL } from '../vectorLayers/constants';
import { updateClusterInfoVL } from '../vectorLayers';

const URL = process.env.REACT_APP_API_URL;

export const setNeedUpdateInfoAction =
  (isUpdate: boolean) => (dispatch: AppDispatch) => {
    dispatch(layersSlice.actions.setNeedUpdateInfo(isUpdate));
  };

export const getLayersAction =
  (isLog: boolean, year = '') =>
  async (dispatch: AppDispatch) => {
    dispatch(layersSlice.actions.setLoading(true));
    let currentYear = year;

    if (!currentYear) {
      const yearUrl = `${URL}/api/${isLog ? 'pv-' : ''}years`;
      const res = await httpClient.post(yearUrl);
      const _years = res.data.data;
      const defaultYear = _years.find((item) => item.default);
      currentYear = defaultYear.year;
      const years = _years.map((item) => item.year);
      dispatch(layersSlice.actions.setYears(years));
    }
    dispatch(layersSlice.actions.setCurrentYear(currentYear));

    const url = `${URL}/api/${isLog ? 'pv-' : ''}layers`;
    const lang = LanguageService.getLanguage();

    const res = await httpClient.post(
      url,
      new URLSearchParams({ lang, year: currentYear }),
      httpConfig
    );

    const _allLayers = res.data.data;

    const allLayers = _allLayers.map((item) => {
      return {
        ...item,
        firstParent: item.tree_id,
      };
    });

    const allChildren = getAllChildren(allLayers).map((item) => {
      return { ...item, label: item.name };
    });

    dispatch(layersSlice.actions.setAllLayers(allLayers));
    dispatch(layersSlice.actions.setAllChildren(allChildren));
    dispatch(layersSlice.actions.setLoading(false));
    dispatch(needUpdateAction(false));

    return { allLayers, allChildren };
  };

export const setCurrentYearAction =
  (year: string) => (dispatch: AppDispatch) => {
    dispatch(layersSlice.actions.setCurrentYear(year));
  };

export const selectATEAction =
  (ate: ISelectedATE | null) => async (dispatch: AppDispatch) => {
    const wktURL = `${URL}/api/getwkt`;
    dispatch(layersSlice.actions.setSelectedATE(ate));

    if (ate) {
      dispatch(setBackdropLoadingAction(true));

      try {
        const res = await httpClient.post(
          wktURL,
          new URLSearchParams({
            id: String(ate.id),
            type: ate.type,
          }),
          httpConfig
        );

        const wkt = res.data.wkt;
        dispatch(layersSlice.actions.setWKT(wkt));
      } catch (e) {
        console.log(e);
      } finally {
        dispatch(setBackdropLoadingAction(false));
      }
    }
  };

export const setSearchWktAction =
  (search: boolean) => (dispatch: AppDispatch) => {
    dispatch(layersSlice.actions.setSearchWkt(search));
  };

export const setCurrentLayerAction =
  (
    layer: string,
    url: string,
    layerId: number,
    legendUrl: string,
    currenMapFileUrl: string,
    metaFile: string,
    parent?: string,
    layerTitle?: string,
    legendSize?: string,
    currentLayer_scale_1_50?: number,
    layer_scale_1_50_vector?: number
  ) =>
  (dispatch: AppDispatch) => {
    dispatch(
      layersSlice.actions.setCurrentLayer({
        layer,
        layerId,
        url,
        legendUrl,
        parent,
        layerTitle,
        legendSize,
        currenMapFileUrl,
        metaFile,
        currentLayer_scale_1_50,
        layer_scale_1_50_vector,
      })
    );
  };

export const resetATEAction = () => (dispatch: AppDispatch) => {
  dispatch(layersSlice.actions.resetSelectedATE());
};

export const setDefaultLayerAction =
  (id: number) => (dispatch: AppDispatch) => {
    dispatch(layersSlice.actions.setDefaultLayer(id));
  };

export const setFoundLayerAction =
  (
    wkt: string,
    info: string,
    coords: number[],
    soato: string,
    zLevel: number
  ) =>
  (dispatch: AppDispatch) => {
    dispatch(layersSlice.actions.setInfo({ wkt, info, coords, soato, zLevel }));
    dispatch(toggleOpenLayerModalAction());
    dispatch(layersSlice.actions.resetSelectedATE());
  };

export const setFoundClusterInfoAction =
  (wkt: string, info: string) => (dispatch: AppDispatch) => {
    dispatch(
      layersSlice.actions.setInfo({
        wkt,
        info,
        coords: [],
        soato: '',
        zLevel: 0,
      })
    );
    dispatch(toggleOpenClusterModalAction(true));
  };

export const resetFoundLayerAction = () => (dispatch: AppDispatch) => {
  dispatch(layersSlice.actions.resetInfo());
};

export const getAteLayerAction =
  (
    ateParam: string,
    parents?: IAteParents,
    addAll?: boolean,
    searchTab?: boolean
  ) =>
  async (dispatch: AppDispatch) => {
    dispatch(setBackdropLoadingAction(true));
    const ateUrl = `${URL}/api/listate`;

    const params = setLastAteParams(parents);
    const allOptions = {
      id: 'all',
      label: getText('SELECT_ALL'),
      name: getText('SELECT_ALL'),
      type: '',
      dataset_name: '',
    };

    if (searchTab) {
      params['type'] = 'search';
    }

    const resAte = await httpClient.post(
      ateUrl,
      new URLSearchParams(params),
      httpConfig
    );

    const listAte = resAte.data.data;
    const _list = listAte.map((item) => {
      return { ...item, label: item.name };
    });

    const list = addAll && _list.length ? [allOptions, ..._list] : _list;

    dispatch(layersSlice.actions.setAte({ param: ateParam, ate: list }));
    dispatch(setBackdropLoadingAction(false));
    dispatch(needUpdateAction(false));
    if (!searchTab) {
      dispatch(layersSlice.actions.setSearchWkt(true));
    }
  };

export const setCurrentAteAction =
  (param: string, ate: ISelectedATE | null) => (dispatch: AppDispatch) => {
    dispatch(layersSlice.actions.setCurrentAte({ param, ate }));
  };

export const resetAteListAction =
  (param: string, value: string) => (dispatch: AppDispatch) => {
    dispatch(layersSlice.actions.resetAte({ param, value }));
  };

export const resetCurrentsAteAction = () => (dispatch: AppDispatch) => {
  dispatch(layersSlice.actions.resetAte({ param: 'currAteRegion', value: '' }));
  dispatch(layersSlice.actions.resetAte({ param: 'currAteCity', value: '' }));
  dispatch(layersSlice.actions.resetAte({ param: 'currAteVC', value: '' }));
};

export const langUpdateAteLayerAction =
  (
    region: ISelectedATE | null,
    city: ISelectedATE | null,
    vc: ISelectedATE | null,
    isOpenFoundInfo: boolean,
    clusterInfoVL?: any,
    searchTab?: boolean
  ) =>
  async (dispatch: AppDispatch) => {
    dispatch(layersSlice.actions.setLoading(true));
    const ateUrl = `${URL}/api/listate`;
    const lang = LanguageService.getLanguage();

    const queryParams = { lang };
    if (searchTab) queryParams['type'] = 'search';

    const resRegion = await httpClient.post(
      ateUrl,
      new URLSearchParams(queryParams),
      httpConfig
    );

    const listRegion = resRegion.data.data.map((item) => {
      return { ...item, label: item.name };
    });

    dispatch(
      layersSlice.actions.setAte({ param: 'ateRegions', ate: listRegion })
    );

    if (region?.id) {
      const resCities = await httpClient.post(
        ateUrl,
        new URLSearchParams({
          ...queryParams,
          'parent[0]': `${region.id}_${region.type}`,
        }),
        httpConfig
      );

      const listCities = resCities.data.data.map((item) => {
        return { ...item, label: item.name };
      });

      const currentRegion = listRegion.find((item) => item.id === region.id);

      dispatch(
        layersSlice.actions.setAte({ param: 'ateCities', ate: listCities })
      );
      dispatch(
        layersSlice.actions.setCurrentAte({
          param: 'currAteRegion',
          ate: currentRegion,
        })
      );

      if (city?.id) {
        const currentCity = listCities.find((item) => item.id === city.id);
        dispatch(
          layersSlice.actions.setCurrentAte({
            param: 'currAteCity',
            ate: currentCity,
          })
        );
      }
    }

    if (city?.id) {
      const resVC = await httpClient.post(
        ateUrl,
        new URLSearchParams({
          ...queryParams,
          'parent[0]': `${city.id}_${city.type}`,
        }),
        httpConfig
      );

      const listVC = resVC.data.data.map((item) => {
        return { ...item, label: item.name };
      });

      dispatch(layersSlice.actions.setAte({ param: 'ateVC', ate: listVC }));

      if (vc?.id) {
        const currentVC = listVC.find((item) => item.id === vc.id);
        dispatch(
          layersSlice.actions.setCurrentAte({
            param: 'currAteVC',
            ate: currentVC,
          })
        );
      }
    }

    // update cluster info vl
    if (clusterInfoVL) {
      const { info } = await getClusterInfoVL(
        clusterInfoVL.isLog,
        clusterInfoVL.gid,
        clusterInfoVL.layerId
      );
      dispatch(updateClusterInfoVL(info));
    }

    // update info modal after changing lang
    if (isOpenFoundInfo) {
      dispatch(setNeedUpdateInfoAction(true));
    }

    dispatch(layersSlice.actions.setLoading(false));
    dispatch(needUpdateAction(false));
  };
